import React, { useEffect, useMemo, useState } from 'react';
import {
  buildDonorSpotlightQueue,
  formatDonorAmount,
  parseDonorAmount,
  parseDonorsCSV
} from '../utils/donors';

const introSlokam = [
  'ఆపదామపహర్తారం దాతారం సర్వసంపదామ్।',
  'లోకాభిరామం శ్రీరామం భూయో భూయో నమామ్యహమ్॥'
];

const spotlightTiers = [
  { id: 'five-lakh', label: 'ఐదు లక్షలకు పైగా', min: 500000, max: Number.POSITIVE_INFINITY },
  { id: 'one-lakh', label: 'ఒక లక్షకు పైగా', min: 100000, max: 500000 },
  { id: 'ten-k', label: 'పది వేలుకు పైగా', min: 10000, max: 100000 },
  { id: 'under-ten-k', label: 'పది వేలులోపు', min: 0, max: 10000, inclusiveMax: true }
];

const Home = ({ onNavigate }) => {
  const [donors, setDonors] = useState([]);
  const [spotlightState, setSpotlightState] = useState({});

  useEffect(() => {
    fetch('/donors.csv')
      .then((res) => res.text())
      .then((text) => {
        const parsed = parseDonorsCSV(text);
        setDonors(parsed);
      })
      .catch((err) => console.error('Failed to load donors.csv', err));
  }, []);

  const spotlightQueues = useMemo(() => {
    return spotlightTiers.reduce((tiers, tier) => {
      const tierDonors = donors.filter((donor) => {
        const amount = parseDonorAmount(donor.amount);
        const matchesMin = tier.min === 0 ? amount >= tier.min : amount > tier.min;
        const matchesMax = tier.inclusiveMax ? amount <= tier.max : amount <= tier.max;
        return matchesMin && matchesMax;
      });

      tiers[tier.id] = buildDonorSpotlightQueue(tierDonors);
      return tiers;
    }, {});
  }, [donors]);

  useEffect(() => {
    const nextState = {};

    spotlightTiers.forEach((tier) => {
      const queue = spotlightQueues[tier.id] || [];
      if (queue.length) {
        nextState[tier.id] = {
          activeDonor: queue[0],
          animationKey: 1,
          isWiping: false
        };
      }
    });

    setSpotlightState(nextState);
  }, [spotlightQueues]);

  useEffect(() => {
    const cleanups = [];

    spotlightTiers.forEach((tier, tierIndex) => {
      const queue = spotlightQueues[tier.id] || [];
      if (queue.length < 2) return;

      let queueIndex = 0;
      let timeoutId;

      const intervalId = window.setInterval(() => {
        setSpotlightState((current) => ({
          ...current,
          [tier.id]: {
            ...current[tier.id],
            isWiping: true
          }
        }));

        timeoutId = window.setTimeout(() => {
          queueIndex = (queueIndex + 1) % queue.length;
          setSpotlightState((current) => ({
            ...current,
            [tier.id]: {
              activeDonor: queue[queueIndex],
              animationKey: (current[tier.id]?.animationKey || 0) + 1,
              isWiping: false
            }
          }));
        }, 420);
      }, 3200 + tierIndex * 350);

      cleanups.push(() => {
        window.clearInterval(intervalId);
        window.clearTimeout(timeoutId);
      });
    });

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [spotlightQueues]);

  return (
    <div className="page-content home-page">
      <div className="slokam-card" aria-label="శ్రీరామ శ్లోకములు">
        <figure className="centered-image-frame">
          <img src="/pattabhisriseetharamaswamy.png" alt="పట్టాభిషేక శ్రీ సీతారామచంద్రస్వామివారి దివ్య చిత్రము" />
        </figure>
        {introSlokam.map((line) => (
          <p key={line} className="intro-slokam">{line}</p>
        ))}
      </div>

      <section className="donor-spotlights-grid" aria-label="దాతల ప్రత్యేక పరిచయం">
        {spotlightTiers.map((tier) => {
          const tierState = spotlightState[tier.id];
          if (!tierState?.activeDonor) return null;

          return (
            <section
              key={tier.id}
              className="donor-spotlight-card"
              aria-label={`${tier.label} దాతల ప్రత్యేక పరిచయం`}
              onClick={() => onNavigate('dathalu')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onNavigate('dathalu');
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className={`donor-spotlight-stage donor-spotlight-stage-clickable ${tierState.isWiping ? 'is-wiping' : ''}`}>
                <div key={tierState.animationKey} className="donor-spotlight-nameplate">
                  <div className="donor-spotlight-meta">
                    <span className="donor-spotlight-tier">{tier.label}</span>
                    <span className="donor-spotlight-amount">₹ {formatDonorAmount(tierState.activeDonor.amount)}</span>
                  </div>
                  <strong className="donor-spotlight-name">{tierState.activeDonor.name}</strong>
                </div>
              </div>
            </section>
          );
        })}
      </section>

      <section className="story-section">
        <div className="section-heading">
          <h2 className="section-title">శ్రీ సీతారామచంద్రస్వామి వారి దేవాలయ పునర్నిర్మాణ మహాసంకల్పం</h2>
          <figure className="temple-plan-frame">
            <img 
              src="/temple-2d.png" 
              alt="శ్రీ సీతారామచంద్రస్వామివారి దేవాలయ నమూనా" 
              className="temple-plan-image" 
            />
          </figure>
          <p className="section-summary">
            వామకుంట్ల గ్రామంలో 108 ఏళ్ల ఆధ్యాత్మిక వారసత్వం కలిగిన రామాలయం, నేడు నూతన వైభవాన్ని సంతరించుకోనుంది. నాడు పరమ భక్తులు గోదా హనుమంతు గారు గ్రామస్తుల మరియు అశేష భక్తజనుల సహకారంతో నిర్మించిన ఆలయం శిథిలావస్థకు చేరడంతో, అదే పవిత్ర స్థలంలో నూతన ఆలయ నిర్మాణానికి శ్రీకారం చుట్టాము. మన పూర్వీకులు అందించిన ఈ దివ్య వారసత్వాన్ని రేపటి తరానికి మరింత వైభవంగా అందించడమే మనందరి దృఢ సంకల్పం. గ్రామస్తులు, ఇతర ప్రాంతాల భక్తులు మరియు ఉదార హృదయాలైన దాతల భాగస్వామ్యంతో ఈ పునర్నిర్మాణ యజ్ఞం అత్యంత భక్తిశ్రద్ధలతో సాగుతోంది. ఈ ధర్మకార్యంలో ప్రతి ఒక్కరూ భాగస్వాములై, ఆ శ్రీరామచంద్రుని కృపాకటాక్షాలు పొందాలని ఆశిస్తున్నాము. ఈ నూతన దేవాలయం వామకుంట్ల గ్రామ కీర్తికి మకుటమై, ఆధ్యాత్మిక జ్యోతిగా విరాజిల్లుతుంది.
          </p>
        </div>

        <article className="story-content">
          <blockquote className="story-quote">
            — రామో విగ్రహవాన్ ధర్మః సత్య ధర్మ పరాక్రమః —
          </blockquote>

          <div className="slokam-panel">
            <div className="slokam-copy-block">
              <p className="tab-copy">
                రామాయణ ఉత్తరకాండలో ధ్యాన శ్లోకముగా చెప్పబడే ఈ మహత్తర శ్లోకం మన స్వామివారి వైభవాన్ని
                కళ్లకు కడుతుంది:
              </p>
              <div className="slokam-verse-block">
                <p className="slokam-verse">వైదేహీసహితం సురద్రుమతలే హైమే మహామంటపే</p>
                <p className="slokam-verse">మధ్యేపుష్పక ఆసనే మణిమయే వీరాసనే సుస్థితమ్ |</p>
                <p className="slokam-verse">అగ్రేవాచయతి ప్రభంజనసుతే తత్త్వం మునిభ్యఃపరం</p>
                <p className="slokam-verse">వ్యాఖ్యాంతం భరతాదిభిః పరివృతం రామం భజే శ్యామలమ్ ||</p>
              </div>
              <p className="tab-copy">
                అంటే... కల్పవృక్షం క్రింద, బంగారు మండపంలో, పుష్పక విమానం మధ్యలో, మణిమయమైన సింహాసనంపై
                వీరాసనంలో కూర్చుని ఉన్నవాడు; తన ముందున్న హనుమంతునికి, మునులకు పరమ తత్వాన్ని బోధిస్తున్నవాడు;
                భరత శత్రుజ్ఞాదులతో పరివేష్టితుడైనవాడు; సీతమ్మతో కలిసి ఉన్న ఆ శ్యామ వర్ణ రాముడిని నేను
                భజిస్తున్నాను.
              </p>
            </div>

            <figure className="hero-image-frame hero-spotlight">
              <img
                className="hero-image"
                src="/sriseetharamaswamy.jpg"
                alt="శ్రీ సీతారామలక్ష్మణ హనుమంతుల దివ్య చిత్రము"
              />
            </figure>
          </div>

          <p className="tab-copy">
            అదే విధంగా మన ఆలయంలో కూడా వీరాసనంలో కూర్చుని, వామభాగంలో సీతమ్మతో, లక్ష్మణ స్వామి సమేతుడై,
            ఎదురుగా భక్త దాసాంజనేయుడు సేవించుకుంటుండగా మన స్వామి భక్తులను రక్షిస్తున్నారు.
          </p>

          <p className="tab-copy">
            ఆగర్భ శ్రీమంతుడు, షోడశ గుణాభిరాముడు అయిన సీతారామచంద్రుని ఆలయ నిర్మాణంలో 
            పాల్గొనడం మన అదృష్టం. ఆయన సంకల్పం లేనిదే ఏదీ జరగదు. కోరిన కోర్కెలు తీర్చే కొంగుబంగారం మన రామచంద్ర స్వామి. ఈ మహత్కార్యంలో ప్రతి ఒక్కరూ తమ వంతు ఉడుత భక్తిగా చేయిచేయీ కలిపి ముందుకు రావాలని
            మనస్ఫూర్తిగా కోరుకుంటున్నాము. </p>
          
          <p className="story-signoff">వామకుంట్ల గ్రామం, తిరువూరు మండలం 521235, NTR జిల్లా, ఆంధ్రప్రదేశ్. </p>
          <p className="story-signoff">— ఆలయ కమిటీ</p>
        </article>
      </section>
    </div>
  );
};

export default Home;
